import React, { useCallback, useState, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { Offer } from '@/src/types/offers'

import { OfferCard } from './offerCard'

dayjs.extend(customParseFormat)

interface OfferCardWrapperProps {
    offer: Offer
}

const OfferCardWrapper: React.FC<OfferCardWrapperProps> = ({ offer }) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('offers')

    const [imgSrc, setImgSrc] = useState(offer.imageUrl)
    const [copied, setCopied] = useState(false)

    const formattedExpiryDate = useMemo(() => {
        if (!offer.expiryDate) return ''
        // Parse DD-MM-YYYY format from API
        const parsed = dayjs(offer.expiryDate, 'DD-MM-YYYY', 'true')
        return parsed.isValid() ? parsed.format('MMM D, YYYY') : offer.expiryDate
    }, [offer.expiryDate])

    const handleImageError = useCallback(() => {
        // Handle image error - could show placeholder or hide image
        setImgSrc('')
    }, [])

    const handleCopyCode = useCallback(() => {
        if (offer.couponCode) {
            navigator.clipboard.writeText(offer.couponCode)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }, [offer.couponCode])

    const handleKnowMore = useCallback(() => {
        router.push(`/${locale}/offers/${offer.slug}`)
    }, [offer.slug, router, locale])

    return (
        <OfferCard
            title={offer.title}
            description={offer.description}
            imageUrl={imgSrc}
            couponCode={offer.couponCode}
            expiryDate={formattedExpiryDate}
            onKnowMore={handleKnowMore}
            onImageError={handleImageError}
            onCopyCode={handleCopyCode}
            copied={copied}
            isActive={offer.isActive}
            expiresOnText={t('expiresOn')}
            knowMoreText={t('knowMore')}
        />
    )
}

export { OfferCardWrapper }
export default OfferCardWrapper
